/*
 * HOME PAGE / DASHBOARD
 * ====================
 * This is the main dashboard page that users see after login.
 * 
 * FEATURES:
 * 1. Workflow Cards - Quick navigation to main modules
 * 2. Recent Activity - Shows latest system activity
 * 3. Stats Display - Shows counts for each module
 * 4. Color-coded modules for easy recognition
 * 
 * TO CUSTOMIZE COLORS:
 * - Header background: Change backgroundColor in the header section
 * - Card colors: Modify the 'color' property in workflowCards array
 * - Card hover effects: Adjust onMouseEnter/onMouseLeave colors
 * 
 * TO CUSTOMIZE STATS:
 * - Edit the 'stats' arrays in workflowCards
 * - Add/remove stat items as needed
 * - Stats are automatically laid out in 3 columns
 * 
 * TO ADD NEW CARDS:
 * - Add new object to workflowCards array
 * - Include: title, description, icon (emoji), color, route, stats
 */

import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '@/components/Auth/AuthContext';
import { useIsNarrowScreen, useMediaQuery } from '@/hooks/useMediaQuery';
import { getCompanyAiValidationUrl, isCompanyAiEnabled } from '@/utils/companyAiAvailability';
import { buildCompanyAiMatchNavigation, buildCompanyAiPeriodSummaryItem } from '@/utils/companyAiNavigation';
import { parseStructuredAnswerBody } from '@/utils/listAnswerHelper';
import { getPrimaryRoleLabel, hasAdminEquivalentAccess, hasRequiredRoleAccess } from '@/utils/swaRole';
import UserNotificationsBell from '@/components/Layout/UserNotificationsBell';
import CompanyAiQuickMenu from './AI/CompanyAiQuickMenu';
import {
    type CompanyAiAgentData,
    type CompanyAiPmContextItem,
    type CompanyAiQueryCandidate,
    type CompanyAiQueryResult,
} from '@/services/companyAiService';
import { pmAgentService, type PmAgentRunResult } from '@/services/pmAgentService';
import { createInitialDashboardCardStats } from './homeDashboardStats';
import './Home.css';

const HOME_DASHBOARD_CACHE_TTL_MS = 60 * 1000;
const HOME_DASHBOARD_STATS_CACHE_KEY = 'project-manager.dashboard.stats.v1';
const HOME_RECENT_ACTIVITY_CACHE_KEY = 'project-manager.dashboard.recent-activity.v1';
const INVOICE_DASHBOARD_SPECIAL_EMAILS = new Set([
    'yl@linfinityeng.com',
    'jp@linfinityeng.com',
    'wd@linfinityeng.com',
]);

type HomeCacheEntry<T> = {
    savedAt: number;
    data: T;
};

type HomeRecentActivityCache = {
    recentColumns: any[];
    activityLastRefreshedAt: string | null;
};

function readHomeCache<T>(cacheKey: string): HomeCacheEntry<T> | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const raw = window.sessionStorage.getItem(cacheKey);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as HomeCacheEntry<T>;
        if (!parsed || typeof parsed.savedAt !== 'number' || !('data' in parsed)) {
            return null;
        }

        return parsed;
    } catch (error) {
        console.warn(`Failed to read dashboard cache for ${cacheKey}:`, error);
        return null;
    }
}

function writeHomeCache<T>(cacheKey: string, data: T) {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const payload: HomeCacheEntry<T> = {
            savedAt: Date.now(),
            data,
        };
        window.sessionStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (error) {
        console.warn(`Failed to write dashboard cache for ${cacheKey}:`, error);
    }
}

function isHomeCacheFresh<T>(cacheEntry: HomeCacheEntry<T> | null): boolean {
    return Boolean(cacheEntry && (Date.now() - cacheEntry.savedAt) < HOME_DASHBOARD_CACHE_TTL_MS);
}

function normalizeRoles(userRoles?: string[] | null) {
    return (userRoles || []).map((role) => String(role || '').trim().toLowerCase()).filter(Boolean);
}

function normalizeEmail(email?: string | null) {
    return String(email || '').trim().toLowerCase();
}

function normalizeErrorMessage(error: unknown) {
    if (error instanceof Error) return error.message;
    return 'Something went wrong.';
}

function isDegradedDashboardPayload(value: any) {
    return Boolean(value?.degraded);
}

function formatQuickAiTimestamp(value?: string | null) {
    if (!value) return 'Just now';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Just now';
    return date.toLocaleString('en-AU', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getPmAgentText(value: unknown) {
    return String(value ?? '').trim();
}

function getPmAgentNumber(value: unknown): number | null {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? Math.trunc(numeric) : null;
}

function clampPmAgentConfidence(value: unknown): number | null {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return Math.max(0, Math.min(1, numeric));
}

function getPmAgentEntityType(toolName: string): CompanyAiQueryCandidate['entityType'] | null {
    if (toolName.includes('Projects')) return 'project';
    if (toolName.includes('Quotations')) return 'quotation';
    if (toolName.includes('Invoices')) return 'invoice';
    if (toolName.includes('Tasks')) return 'task';
    if (toolName.includes('Clients')) return 'client';
    return null;
}

function getPmAgentRecordId(entityType: CompanyAiQueryCandidate['entityType'], row: Record<string, unknown>) {
    switch (entityType) {
        case 'project':
            return getPmAgentNumber(row.projectId);
        case 'quotation':
            return getPmAgentNumber(row.quotationId);
        case 'invoice':
            return getPmAgentNumber(row.invoiceId);
        case 'task':
            return getPmAgentNumber(row.taskId);
        case 'client':
            return getPmAgentNumber(row.clientId);
        default:
            return null;
    }
}

function buildPmAgentCandidateTitle(entityType: CompanyAiQueryCandidate['entityType'], row: Record<string, unknown>, entityId: number) {
    const title = getPmAgentText(row.title);
    if (title) return title;

    switch (entityType) {
        case 'project': {
            const projectNumber = getPmAgentText(row.projectNumber);
            return projectNumber ? `Project ${projectNumber}` : `Project ${entityId}`;
        }
        case 'quotation': {
            const quotationNumber = getPmAgentText(row.quotationNumber);
            return quotationNumber ? `Quotation ${quotationNumber}` : `Quotation ${entityId}`;
        }
        case 'invoice': {
            const invoiceNumber = getPmAgentText(row.invoiceNumber);
            return invoiceNumber ? `Invoice ${invoiceNumber}` : `Invoice ${entityId}`;
        }
        case 'task': {
            const taskNumber = getPmAgentText(row.taskNumber);
            return taskNumber ? `Task ${taskNumber}` : `Task ${entityId}`;
        }
        case 'client':
            return getPmAgentText(row.company) || getPmAgentText(row.name) || `Client ${entityId}`;
        default:
            return `${entityType} ${entityId}`;
    }
}

function buildPmAgentCandidateSubtitle(entityType: CompanyAiQueryCandidate['entityType'], row: Record<string, unknown>) {
    const summary = getPmAgentText(row.summary);
    if (summary) return summary;

    switch (entityType) {
        case 'project':
            return [
                getPmAgentText(row.clientName) ? `client ${getPmAgentText(row.clientName)}` : null,
                [getPmAgentText(row.address), getPmAgentText(row.suburb)].filter(Boolean).join(', '),
                getPmAgentText(row.status) ? `status ${getPmAgentText(row.status)}` : null,
            ].filter(Boolean).join('; ');
        case 'quotation':
            return [
                getPmAgentText(row.clientName) ? `client ${getPmAgentText(row.clientName)}` : null,
                getPmAgentText(row.status) ? `status ${getPmAgentText(row.status)}` : null,
                getPmAgentText(row.createdDate) ? `created ${getPmAgentText(row.createdDate)}` : null,
            ].filter(Boolean).join('; ');
        case 'invoice':
            return [
                getPmAgentText(row.clientName) ? `client ${getPmAgentText(row.clientName)}` : null,
                getPmAgentText(row.projectNumber) ? `project ${getPmAgentText(row.projectNumber)}` : null,
                getPmAgentText(row.dueDate) ? `due ${getPmAgentText(row.dueDate)}` : null,
                getPmAgentText(row.paymentStatus) ? `payment ${getPmAgentText(row.paymentStatus)}` : null,
            ].filter(Boolean).join('; ');
        case 'task':
            return [
                getPmAgentText(row.clientName) ? `client ${getPmAgentText(row.clientName)}` : null,
                getPmAgentText(row.projectNumber) ? `project ${getPmAgentText(row.projectNumber)}` : null,
                getPmAgentText(row.status) ? `status ${getPmAgentText(row.status)}` : null,
                getPmAgentText(row.dueDate) ? `due ${getPmAgentText(row.dueDate)}` : null,
            ].filter(Boolean).join('; ');
        case 'client':
            return [
                getPmAgentText(row.name) && getPmAgentText(row.company) && getPmAgentText(row.name) !== getPmAgentText(row.company)
                    ? getPmAgentText(row.name)
                    : null,
                getPmAgentText(row.email),
                getPmAgentText(row.address),
            ].filter(Boolean).join('; ');
        default:
            return '';
    }
}

function buildPmAgentCandidate(entityType: CompanyAiQueryCandidate['entityType'], row: Record<string, unknown>): CompanyAiQueryCandidate | null {
    const entityId = getPmAgentRecordId(entityType, row);
    if (!entityId) return null;

    const routePath = getPmAgentText(row.routePath) || {
        project: '/projects',
        quotation: '/quotations',
        invoice: '/invoices',
        task: '/tasks',
        client: '/clients',
        certificate: '/certificates',
        byda: '/ai',
    }[entityType];

    return {
        entityType,
        entityId,
        routePath,
        title: buildPmAgentCandidateTitle(entityType, row, entityId),
        subtitle: buildPmAgentCandidateSubtitle(entityType, row),
        projectNumber: getPmAgentText(row.projectNumber) || null,
        quotationNumber: getPmAgentText(row.quotationNumber) || null,
        invoiceNumber: getPmAgentText(row.invoiceNumber) || null,
        taskNumber: getPmAgentText(row.taskNumber) || null,
        address: getPmAgentText(row.address || row.projectAddress) || null,
        suburb: getPmAgentText(row.suburb || row.projectSuburb) || null,
        status: getPmAgentText(row.status || row.paymentStatus) || null,
        confidence: 0.82,
    };
}

function getPmAgentCandidates(result: PmAgentRunResult): CompanyAiQueryCandidate[] {
    const entityType = getPmAgentEntityType(result.selectedTool);
    if (!entityType || result.result.resultType !== 'rows') {
        return [];
    }

    return (result.result.rows || [])
        .map((row) => buildPmAgentCandidate(entityType, row))
        .filter((candidate): candidate is CompanyAiQueryCandidate => Boolean(candidate));
}

function formatPmAgentDate(date: Date) {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function resolvePmAgentDateRange(args: Record<string, unknown>) {
    const startDate = getPmAgentText(args.startDate);
    const endDate = getPmAgentText(args.endDate);
    if (startDate || endDate) {
        return {
            startDate: startDate || null,
            endDate: endDate || null,
            label: getPmAgentText(args.period) || 'custom',
        };
    }

    const period = getPmAgentText(args.period);
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

    if (period === 'this_month') {
        return {
            startDate: formatPmAgentDate(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))),
            endDate: formatPmAgentDate(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0))),
            label: 'this month',
        };
    }

    if (period === 'this_year') {
        return {
            startDate: formatPmAgentDate(new Date(Date.UTC(today.getUTCFullYear(), 0, 1))),
            endDate: formatPmAgentDate(new Date(Date.UTC(today.getUTCFullYear(), 11, 31))),
            label: 'this year',
        };
    }

    if (period === 'last_30_days') {
        const start = new Date(today);
        start.setUTCDate(today.getUTCDate() - 30);
        return {
            startDate: formatPmAgentDate(start),
            endDate: formatPmAgentDate(today),
            label: 'last 30 days',
        };
    }

    return null;
}

function getPmAgentEntityHints(toolName: string): string[] {
    const entityType = getPmAgentEntityType(toolName);
    if (!entityType) {
        return toolName === 'businessAnalysis' ? ['invoice'] : [];
    }
    return [entityType];
}

function buildPmAgentInvoiceSummary(result: PmAgentRunResult): CompanyAiAgentData['invoiceSummary'] | undefined {
    if (result.selectedTool !== 'aggregateInvoices') {
        return undefined;
    }

    const row = result.result.rows[0] || {};
    return {
        title: 'Invoice summary',
        summary: getPmAgentText(result.result.facts?.summary) || result.summary || result.answer,
        routePath: '/invoices',
        measure: getPmAgentText(result.result.facts?.measure) || 'invoices',
        aggregation: getPmAgentText(result.result.facts?.aggregation) || 'amount',
        periodLabel: getPmAgentText(result.arguments?.period) || null,
        periodStart: getPmAgentText(row.periodStart) || null,
        periodEnd: getPmAgentText(row.periodEnd) || null,
        dateField: getPmAgentText(result.arguments?.dateField || result.arguments?.field) || null,
        totalInvoicedAmount: Number(row.totalInvoicedAmount || 0),
        totalReceivables: Number(row.totalReceivables || 0),
        overdueReceivables: Number(row.overdueReceivables || 0),
        paidAmount: Number(row.paidAmount || 0),
        totalInvoiceCount: Number(row.totalInvoiceCount || 0),
        openInvoiceCount: Number(row.openInvoiceCount || 0),
        overdueInvoiceCount: Number(row.overdueInvoiceCount || 0),
        paidInvoiceCount: Number(row.paidInvoiceCount || 0),
        averageInvoiceAmount: 0,
        averageReceivableAmount: 0,
        averageOverdueAmount: 0,
        averagePaidAmount: 0,
    };
}

function buildPmAgentData(result: PmAgentRunResult, message: string): CompanyAiAgentData {
    const dateRange = resolvePmAgentDateRange(result.arguments || {});
    const agentData: CompanyAiAgentData = {
        querySpec: {
            primaryIntent: result.intent || result.selectedTool,
            rawQuery: message,
            temporal: dateRange ? [{
                field: getPmAgentText(result.arguments?.dateField || result.arguments?.field) || undefined,
                start: dateRange.startDate || undefined,
                end: dateRange.endDate || undefined,
                relativeLabel: dateRange.label,
                rawText: dateRange.label,
            }] : [],
            filters: result.arguments || {},
            aggregation: {
                groupBy: getPmAgentText(result.arguments?.groupBy) || undefined,
                sortBy: result.arguments?.latestOnly ? 'date' : undefined,
                sortDir: result.arguments?.latestOnly ? 'desc' : undefined,
            },
        },
    };

    const rows = result.result.rows || [];
    switch (result.selectedTool) {
        case 'queryProjects':
            agentData.projects = rows as CompanyAiAgentData['projects'];
            break;
        case 'queryQuotations':
            agentData.quotations = rows as CompanyAiAgentData['quotations'];
            break;
        case 'queryInvoices':
            agentData.invoices = rows as CompanyAiAgentData['invoices'];
            break;
        case 'queryTasks':
            agentData.tasks = rows as CompanyAiAgentData['tasks'];
            break;
        case 'queryClients':
            agentData.clients = rows as CompanyAiAgentData['clients'];
            break;
        case 'aggregateInvoices':
            agentData.invoiceSummary = buildPmAgentInvoiceSummary(result);
            break;
        default:
            break;
    }

    return agentData;
}

function mapPmAgentResultToQuickQueryResult(message: string, result: PmAgentRunResult): CompanyAiQueryResult {
    const candidates = getPmAgentCandidates(result);
    const selectedCandidate = candidates[0] || null;
    const selectedEntityType = selectedCandidate?.entityType || getPmAgentEntityType(result.selectedTool);
    const selectedEntityId = selectedCandidate?.entityId || null;
    const confidence = clampPmAgentConfidence(result.debug?.intent?.confidence);

    return {
        sessionId: `pm-agent-${Date.now()}`,
        actionType: 'answer',
        answer: result.summary || result.answer || 'PM Agent returned no summary.',
        confidence,
        clarificationQuestion: null,
        candidates,
        selectedEntityType,
        selectedEntityId,
        stateSummary: {
            currentScope: selectedEntityType,
            clarificationCount: 0,
            selectedEntityType,
            selectedEntityId,
            candidateCount: candidates.length,
        },
        plan: {
            normalizedQuery: message.trim().toLowerCase(),
            entityTypeHints: getPmAgentEntityHints(result.selectedTool),
            intent: result.intent || result.selectedTool,
            constraints: {
                tool: result.selectedTool,
                arguments: result.arguments,
                resultType: result.result.resultType,
            },
            dateRange: (() => {
                const range = resolvePmAgentDateRange(result.arguments || {});
                return {
                    startDate: range?.startDate || null,
                    endDate: range?.endDate || null,
                    dateField: getPmAgentText(result.arguments?.dateField || result.arguments?.field) || null,
                    sourcePhrase: range?.label || null,
                };
            })(),
            relationHops: [],
            sortPreference: result.arguments?.latestOnly ? 'date' : 'pm-agent',
        },
        agentSteps: [{
            tool: result.selectedTool,
            success: true,
            summary: `PM Agent ran ${result.selectedTool}.`,
            inputSummary: message,
            outputSummary: `${result.result.rowCount} row${result.result.rowCount === 1 ? '' : 's'}`,
            confidence: confidence ?? undefined,
        }],
        agentData: buildPmAgentData(result, message),
        citations: [{
            sourceType: 'live_pmapp',
            label: `PM Agent ${result.selectedTool}`,
            detail: `${result.result.resultType} result`,
            routePath: null,
        }],
    };
}

type QuickAiSectionBlock = {
    type: 'paragraph' | 'list';
    lines: string[];
};

type RankedQuickAiPmMatch = {
    item: CompanyAiPmContextItem;
    confidenceValue: number | null;
    confidenceLabel: string | null;
    isTopMatch: boolean;
};

type QuickAiAnswerNavigationTarget = {
    item: CompanyAiPmContextItem;
    actionLabel: string;
};

type QuickAiCollectionNavigationTarget = QuickAiAnswerNavigationTarget & {
    resultCount: number;
    domainLabel: string;
};

function normalizeQuickAiReferenceText(value: unknown) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function splitQuickAiSectionBody(body: string): QuickAiSectionBlock[] {
    return parseStructuredAnswerBody(body).map((block) => ({
        type: block.type === 'paragraph' ? 'paragraph' : 'list',
        lines: block.items,
    }));
}

function renderQuickAiSectionBody(body: string) {
    const blocks = splitQuickAiSectionBody(body);
    if (!blocks.length) {
        return null;
    }

    return blocks.map((block, blockIndex) => {
        if (block.type === 'list') {
            return (
                <ul key={`quick-ai-block-${blockIndex}`} className="home-ai-quick-card__answer-list-block">
                    {block.lines.map((line, lineIndex) => (
                        <li key={`quick-ai-line-${blockIndex}-${lineIndex}`}>{line}</li>
                    ))}
                </ul>
            );
        }

        return (
            <p key={`quick-ai-block-${blockIndex}`} className="home-ai-quick-card__answer-paragraph">
                {block.lines.map((line, lineIndex) => (
                    <React.Fragment key={`quick-ai-line-${blockIndex}-${lineIndex}`}>
                        {lineIndex > 0 ? <br /> : null}
                        {line}
                    </React.Fragment>
                ))}
            </p>
        );
    });
}

function getQuickAiMatchActionLabel(item: CompanyAiPmContextItem) {
    if (item.recordId == null) {
        switch (item.domain) {
            case 'projects':
                return 'Open filtered project list';
            case 'quotations':
                return 'Open filtered quotation list';
            case 'tasks':
                return 'Open filtered task list';
            case 'invoices':
                return 'Open filtered invoice list';
            default:
                return 'Open filtered list';
        }
    }

    switch (item.domain) {
        case 'projects':
            return 'Open project row';
        case 'quotations':
            return 'Open quotation row';
        case 'tasks':
            return 'Open task row';
        case 'invoices':
            return 'Open invoice row';
        default:
            return 'Open record';
    }
}

function formatQuickAiMatchPercent(value?: number | null) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return null;
    }

    return `${Math.max(0, Math.round(value * 100))}%`;
}

function buildQuickAiStructuredItem(candidate: CompanyAiQueryCandidate): CompanyAiPmContextItem {
    return {
        domain: getQuickAiCandidateDomain(candidate.entityType),
        recordId: candidate.entityId,
        title: candidate.title || `${candidate.entityType} ${candidate.entityId}`,
        summary: candidate.subtitle || 'No summary available.',
        routePath: candidate.routePath || null,
    };
}

function getRenderableStructuredCandidates(result: CompanyAiQueryResult | null): CompanyAiQueryCandidate[] {
    return (result?.candidates || []).filter((candidate) => {
        const confidence = typeof candidate.confidence === 'number' ? candidate.confidence : null;
        const score = typeof candidate.score === 'number' ? candidate.score : null;
        return (confidence !== null && confidence >= 0.05) || (score !== null && score > 0);
    });
}

function getQuickAiCandidateDomain(entityType: CompanyAiQueryCandidate['entityType']): CompanyAiPmContextItem['domain'] {
    switch (entityType) {
        case 'project':
            return 'projects';
        case 'client':
            return 'clients';
        case 'quotation':
            return 'quotations';
        case 'invoice':
            return 'invoices';
        case 'certificate':
            return 'certificates';
        case 'byda':
            return 'byda';
        case 'task':
        default:
            return 'tasks';
    }
}

function getQuickAiPreferredDomains(
    prompt: string | null | undefined,
    structuredMatch: CompanyAiQueryResult | null
): CompanyAiPmContextItem['domain'][] {
    const normalizedPrompt = normalizeQuickAiReferenceText(prompt);
    const hints = Array.isArray(structuredMatch?.plan?.entityTypeHints) ? structuredMatch!.plan.entityTypeHints : [];
    const orderedHints = hints.length ? hints : [
        normalizedPrompt.includes('client') || normalizedPrompt.includes('clients') ? 'client' : null,
        normalizedPrompt.includes('certificate') || normalizedPrompt.includes('certificates') ? 'certificate' : null,
        normalizedPrompt.includes('byda') || normalizedPrompt.includes('dbyd') ? 'byda' : null,
        normalizedPrompt.includes('invoice') || normalizedPrompt.includes('invoices') || normalizedPrompt.includes(' inv ') ? 'invoice' : null,
        normalizedPrompt.includes('quotation') || normalizedPrompt.includes('quotations') || normalizedPrompt.includes(' quote ') || normalizedPrompt.includes(' quotes ') ? 'quotation' : null,
        normalizedPrompt.includes('project') || normalizedPrompt.includes('projects') || normalizedPrompt.includes(' job ') || normalizedPrompt.includes(' jobs ') ? 'project' : null,
        normalizedPrompt.includes('task') || normalizedPrompt.includes('tasks') || normalizedPrompt.includes('rfi') || normalizedPrompt.includes('rfis') ? 'task' : null,
    ].filter(Boolean) as Array<CompanyAiQueryCandidate['entityType'] | 'knowledge'>;

    const domainOrder = new Map<CompanyAiPmContextItem['domain'], number>();
    const pushDomain = (domain: CompanyAiPmContextItem['domain']) => {
        if (!domainOrder.has(domain)) {
            domainOrder.set(domain, domainOrder.size);
        }
    };

    orderedHints.forEach((hint) => {
        switch (hint) {
            case 'invoice':
                pushDomain('invoices');
                pushDomain('quotations');
                pushDomain('projects');
                pushDomain('tasks');
                break;
            case 'client':
                pushDomain('clients');
                pushDomain('projects');
                pushDomain('invoices');
                pushDomain('quotations');
                break;
            case 'quotation':
                pushDomain('quotations');
                pushDomain('invoices');
                pushDomain('projects');
                pushDomain('tasks');
                break;
            case 'certificate':
                pushDomain('certificates');
                pushDomain('projects');
                break;
            case 'byda':
                pushDomain('byda');
                pushDomain('projects');
                break;
            case 'project':
                pushDomain('projects');
                pushDomain('quotations');
                pushDomain('invoices');
                pushDomain('tasks');
                break;
            case 'task':
                pushDomain('tasks');
                pushDomain('projects');
                pushDomain('quotations');
                pushDomain('invoices');
                break;
            default:
                break;
        }
    });

    if (!domainOrder.size && structuredMatch?.selectedEntityType) {
        pushDomain(getQuickAiCandidateDomain(structuredMatch.selectedEntityType));
    }

    ['projects', 'quotations', 'invoices', 'tasks', 'clients', 'certificates', 'byda'].forEach((domain) => pushDomain(domain as CompanyAiPmContextItem['domain']));
    return Array.from(domainOrder.keys());
}

function buildRankedQuickAiStructuredMatches(
    candidates: CompanyAiQueryCandidate[],
    selectedEntity?: {
        entityType: CompanyAiQueryResult['selectedEntityType'];
        entityId: CompanyAiQueryResult['selectedEntityId'];
    } | null,
    preferredDomains: CompanyAiPmContextItem['domain'][] = []
): RankedQuickAiPmMatch[] {
    const rankedCandidates = [...candidates].sort((left, right) => {
        const leftScore = typeof left.confidence === 'number' ? left.confidence : (typeof left.score === 'number' ? left.score : -1);
        const rightScore = typeof right.confidence === 'number' ? right.confidence : (typeof right.score === 'number' ? right.score : -1);
        return rightScore - leftScore;
    });
    const rankedMatches = rankedCandidates.map((candidate, index) => {
        const item = buildQuickAiStructuredItem(candidate);
        const confidenceValue = typeof candidate?.confidence === 'number'
            ? candidate.confidence
            : (typeof candidate?.score === 'number' ? candidate.score : null);
        const isSelectedEntity = Boolean(
            candidate.entityId != null
            && selectedEntity?.entityId != null
            && selectedEntity.entityType
            && getQuickAiCandidateDomain(selectedEntity.entityType) === item.domain
            && selectedEntity.entityId === candidate.entityId
        );
        const preferredRank = Math.max(0, preferredDomains.indexOf(item.domain));

        return {
            item,
            confidenceValue,
            confidenceLabel: formatQuickAiMatchPercent(confidenceValue),
            isTopMatch: isSelectedEntity,
            selectedRank: isSelectedEntity ? 0 : 1,
            preferredRank,
            originalIndex: index,
        };
    }).sort((left, right) => {
        if (left.selectedRank !== right.selectedRank) {
            return left.selectedRank - right.selectedRank;
        }
        if (left.preferredRank !== right.preferredRank) {
            return left.preferredRank - right.preferredRank;
        }
        const leftScore = left.confidenceValue ?? -1;
        const rightScore = right.confidenceValue ?? -1;
        if (rightScore !== leftScore) {
            return rightScore - leftScore;
        }
        return left.originalIndex - right.originalIndex;
    });

    const topMatchIndex = rankedMatches.findIndex((entry) => entry.isTopMatch || entry.confidenceValue !== null);
    if (topMatchIndex >= 0) {
        rankedMatches[topMatchIndex] = {
            ...rankedMatches[topMatchIndex],
            isTopMatch: true,
        };
    }

    return rankedMatches.map(({ originalIndex: _originalIndex, selectedRank: _selectedRank, preferredRank: _preferredRank, ...entry }) => entry);
}

function getQuickAiDomainLabel(domain: CompanyAiPmContextItem['domain']) {
    switch (domain) {
        case 'projects':
            return 'project';
        case 'quotations':
            return 'quotation';
        case 'invoices':
            return 'invoice';
        case 'clients':
            return 'client';
        case 'certificates':
            return 'certificate';
        case 'byda':
            return 'BYDA record';
        case 'tasks':
        default:
            return 'task';
    }
}

function buildQuickAiStructuredSections(result: CompanyAiQueryResult | null) {
    if (!result) {
        return [];
    }

    const answer = String(result.answer || '').trim();
    const clarificationQuestion = String(result.clarificationQuestion || '').trim();
    const sections: Array<{ heading: string; body: string }> = [];

    if (answer) {
        sections.push({
            heading: result.actionType === 'clarify' ? 'Need one detail' : 'Answer',
            body: answer,
        });
    }

    if (clarificationQuestion && clarificationQuestion !== answer) {
        sections.push({
            heading: 'Next step',
            body: clarificationQuestion,
        });
    }

    return sections;
}

function buildQuickAiResultBadgeLabel(
    topMatch: RankedQuickAiPmMatch | null,
    candidateCount: number,
    collectionTarget?: QuickAiCollectionNavigationTarget | null
) {
    if (collectionTarget) {
        return `${collectionTarget.resultCount} ${collectionTarget.domainLabel}`;
    }

    if (topMatch?.confidenceLabel) {
        return `Top match ${topMatch.confidenceLabel}`;
    }

    return buildQuickAiMatchCountLabel(candidateCount);
}

function buildQuickAiCollectionCaption(collectionTarget: QuickAiCollectionNavigationTarget | null) {
    if (!collectionTarget) {
        return null;
    }

    return `${collectionTarget.resultCount} ${collectionTarget.domainLabel}`;
}

function buildQuickAiTopMatchCaption(topMatch: RankedQuickAiPmMatch | null) {
    if (!topMatch) {
        return null;
    }

    const confidenceText = topMatch.confidenceLabel ? ` ${topMatch.confidenceLabel}` : '';
    return `Top ${getQuickAiDomainLabel(topMatch.item.domain)} match${confidenceText}`;
}

function pluralizeQuickAiDomainLabel(domain: CompanyAiPmContextItem['domain']) {
    switch (domain) {
        case 'byda':
            return 'BYDA records';
        case 'clients':
            return 'clients';
        case 'certificates':
            return 'certificates';
        case 'invoices':
            return 'invoices';
        case 'projects':
            return 'projects';
        case 'quotations':
            return 'quotations';
        case 'tasks':
        default:
            return 'tasks';
    }
}

function getFirstQuickAiText(value: unknown) {
    if (Array.isArray(value)) {
        return String(value.find((item) => String(item || '').trim()) || '').trim();
    }

    return String(value || '').trim();
}

function getQuickAiCollectionFilterText(
    result: CompanyAiQueryResult,
    domain: CompanyAiPmContextItem['domain']
) {
    const filters = result.agentData?.querySpec?.filters || {};
    const genericDisplayText = getFirstQuickAiText(filters.text)
        || getFirstQuickAiText(filters.client)
        || getFirstQuickAiText(filters.suburb)
        || getFirstQuickAiText(filters.council)
        || getFirstQuickAiText(filters.projectNumber)
        || getFirstQuickAiText(filters.invoiceNo)
        || getFirstQuickAiText(filters.quotationNo)
        || getFirstQuickAiText(filters.taskNo)
        || getFirstQuickAiText(filters.status);
    const genericSearchText = getFirstQuickAiText(filters.text)
        || getFirstQuickAiText(filters.projectNumber)
        || getFirstQuickAiText(filters.invoiceNo)
        || getFirstQuickAiText(filters.quotationNo)
        || getFirstQuickAiText(filters.taskNo)
        || getFirstQuickAiText(filters.client)
        || getFirstQuickAiText(filters.suburb)
        || getFirstQuickAiText(filters.council)
        || getFirstQuickAiText(filters.status);

    if (domain === 'tasks') {
        const displayText = getFirstQuickAiText(filters.assignedToUserLabels)
            || getFirstQuickAiText(filters.assignedToUserEmails)
            || getFirstQuickAiText(filters.ownerUserLabels)
            || getFirstQuickAiText(filters.ownerUserEmails)
            || getFirstQuickAiText(filters.createdByUserLabels)
            || getFirstQuickAiText(filters.createdByUserEmails);
        const searchText = getFirstQuickAiText(filters.assignedToUserEmails)
            || getFirstQuickAiText(filters.assignedToUserLabels)
            || getFirstQuickAiText(filters.ownerUserEmails)
            || getFirstQuickAiText(filters.ownerUserLabels)
            || getFirstQuickAiText(filters.createdByUserEmails)
            || getFirstQuickAiText(filters.createdByUserLabels);

        return {
            displayText: displayText || genericDisplayText,
            searchText: searchText || genericSearchText,
        };
    }

    return {
        displayText: genericDisplayText,
        searchText: genericSearchText,
    };
}

function buildQuickAiCollectionNavigationTarget(result: CompanyAiQueryResult | null): QuickAiCollectionNavigationTarget | null {
    if (!result || result.actionType !== 'answer') {
        return null;
    }

    const data = result.agentData;
    const collections: Array<{
        domain: CompanyAiPmContextItem['domain'];
        count: number;
    }> = [
        { domain: 'tasks', count: Array.isArray(data?.tasks) ? data!.tasks.length : 0 },
        { domain: 'projects', count: Array.isArray(data?.projects) ? data!.projects.length : 0 },
        { domain: 'invoices', count: Array.isArray(data?.invoices) ? data!.invoices.length : 0 },
        { domain: 'quotations', count: Array.isArray(data?.quotations) ? data!.quotations.length : 0 },
        { domain: 'clients', count: Array.isArray(data?.clients) ? data!.clients.length : 0 },
        { domain: 'certificates', count: Array.isArray(data?.certificates) ? data!.certificates.length : 0 },
        { domain: 'byda', count: Array.isArray(data?.byda) ? data!.byda.length : 0 },
    ];
    const collection = collections.find((entry) => entry.count > 1);
    if (!collection) {
        return null;
    }

    const domainLabel = pluralizeQuickAiDomainLabel(collection.domain);
    const filterText = getQuickAiCollectionFilterText(result, collection.domain);
    const periodSummaryItem = buildCompanyAiPeriodSummaryItem(collection.domain, result.plan?.dateRange);
    const item: CompanyAiPmContextItem = periodSummaryItem || {
        domain: collection.domain,
        recordId: null,
        title: filterText.displayText
            ? `${domainLabel[0].toUpperCase()}${domainLabel.slice(1)} for ${filterText.displayText}`
            : `${domainLabel[0].toUpperCase()}${domainLabel.slice(1)} summary`,
        summary: filterText.searchText,
        routePath: null,
    };

    return {
        item,
        actionLabel: getQuickAiMatchActionLabel(item),
        resultCount: collection.count,
        domainLabel,
    };
}

function buildQuickAiInvoiceSummaryNavigationTarget(result: CompanyAiQueryResult | null): QuickAiAnswerNavigationTarget | null {
    const invoiceSummary = result?.agentData?.invoiceSummary;
    if (!result || !invoiceSummary) {
        return null;
    }

    const periodSummaryItem = buildCompanyAiPeriodSummaryItem('invoices', result.plan?.dateRange);
    const item: CompanyAiPmContextItem = periodSummaryItem || {
        domain: 'invoices',
        recordId: null,
        title: invoiceSummary.title || 'Invoice summary',
        summary: invoiceSummary.summary || '',
        routePath: invoiceSummary.routePath || '/invoices',
    };

    return {
        item,
        actionLabel: getQuickAiMatchActionLabel(item),
    };
}

function buildQuickAiAnswerNavigationTarget(
    result: CompanyAiQueryResult | null,
    topMatch: RankedQuickAiPmMatch | null,
    collectionTarget?: QuickAiCollectionNavigationTarget | null
): QuickAiAnswerNavigationTarget | null {
    if (!result || result.actionType !== 'answer') {
        return null;
    }

    if (collectionTarget) {
        return collectionTarget;
    }

    const invoiceSummaryTarget = buildQuickAiInvoiceSummaryNavigationTarget(result);
    if (invoiceSummaryTarget) {
        return invoiceSummaryTarget;
    }

    if (!topMatch) {
        return null;
    }

    const periodSummaryItem = buildCompanyAiPeriodSummaryItem(topMatch.item.domain, result.plan?.dateRange);
    if (periodSummaryItem) {
        return {
            item: periodSummaryItem,
            actionLabel: getQuickAiMatchActionLabel(periodSummaryItem),
        };
    }

    return {
        item: topMatch.item,
        actionLabel: getQuickAiMatchActionLabel(topMatch.item),
    };
}

function splitQuickAiSummaryParts(summary: string) {
    const parts = String(summary || '')
        .split(';')
        .map((part) => part.trim())
        .filter(Boolean);

    const leadParts: string[] = [];
    const metricParts: string[] = [];

    parts.forEach((part) => {
        if (/^(for |period |by )/i.test(part)) {
            leadParts.push(part);
            return;
        }
        metricParts.push(part);
    });

    if (!leadParts.length && metricParts.length > 1) {
        leadParts.push(metricParts.shift() as string);
    }

    return {
        leadParts,
        metricParts,
    };
}

type QuickAiReturnedRowCard = {
    key: string;
    item: CompanyAiPmContextItem;
    title: string;
    lead: string;
    metrics: string[];
    confidenceLabel: string | null;
};

function formatQuickAiMoney(value: unknown) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return null;
    }

    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    }).format(numeric);
}

function getQuickAiAgentCollection(result: CompanyAiQueryResult | null): {
    domain: CompanyAiPmContextItem['domain'];
    rows: Array<Record<string, unknown>>;
} | null {
    const data = result?.agentData;
    const collections: Array<{
        domain: CompanyAiPmContextItem['domain'];
        rows?: unknown[];
    }> = [
        { domain: 'invoices', rows: data?.invoices as unknown[] | undefined },
        { domain: 'projects', rows: data?.projects as unknown[] | undefined },
        { domain: 'quotations', rows: data?.quotations as unknown[] | undefined },
        { domain: 'tasks', rows: data?.tasks as unknown[] | undefined },
        { domain: 'clients', rows: data?.clients as unknown[] | undefined },
    ];
    const collection = collections.find((entry) => Array.isArray(entry.rows) && entry.rows.length > 0);
    if (!collection?.rows?.length) {
        return null;
    }

    return {
        domain: collection.domain,
        rows: collection.rows.filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object' && !Array.isArray(row))),
    };
}

function getQuickAiAgentRowId(domain: CompanyAiPmContextItem['domain'], row: Record<string, unknown>) {
    switch (domain) {
        case 'projects':
            return getPmAgentNumber(row.projectId);
        case 'quotations':
            return getPmAgentNumber(row.quotationId);
        case 'tasks':
            return getPmAgentNumber(row.taskId);
        case 'clients':
            return getPmAgentNumber(row.clientId);
        case 'invoices':
        default:
            return getPmAgentNumber(row.invoiceId);
    }
}

function buildQuickAiItemFromAgentRow(
    domain: CompanyAiPmContextItem['domain'],
    row: Record<string, unknown>,
    recordId: number | null,
    title: string,
    lead: string
): CompanyAiPmContextItem {
    return {
        domain,
        recordId,
        title,
        summary: lead,
        routePath: getPmAgentText(row.routePath) || null,
    };
}

function buildQuickAiRawRowCard(
    domain: CompanyAiPmContextItem['domain'],
    row: Record<string, unknown>,
    index: number,
    match?: RankedQuickAiPmMatch
): QuickAiReturnedRowCard {
    const recordId = getQuickAiAgentRowId(domain, row);
    const money = formatQuickAiMoney(row.amount || row.totalReceivable || row.totalReceivables || row.paidAmount);

    if (domain === 'invoices') {
        const title = getPmAgentText(row.invoiceNumber) || (recordId ? `Invoice ${recordId}` : `Invoice row ${index + 1}`);
        const lead = [
            getPmAgentText(row.clientName),
            getPmAgentText(row.projectAddress),
        ].filter(Boolean).join(' · ');
        const metrics = [
            money,
            getPmAgentText(row.paymentStatus || row.status) ? `payment ${getPmAgentText(row.paymentStatus || row.status)}` : '',
            getPmAgentText(row.paidDate) ? `paid ${getPmAgentText(row.paidDate)}` : '',
            getPmAgentText(row.dueDate) ? `due ${getPmAgentText(row.dueDate)}` : '',
        ].filter(Boolean);

        return {
            key: `${domain}-${recordId ?? index}`,
            item: match?.item || buildQuickAiItemFromAgentRow(domain, row, recordId, title, lead),
            title,
            lead,
            metrics,
            confidenceLabel: match?.confidenceLabel || null,
        };
    }

    const title = getPmAgentText(row.title)
        || getPmAgentText(row.projectNumber)
        || getPmAgentText(row.quotationNumber)
        || getPmAgentText(row.taskNumber)
        || getPmAgentText(row.company)
        || getPmAgentText(row.name)
        || `${getQuickAiDomainLabel(domain)} row ${index + 1}`;
    const lead = [
        getPmAgentText(row.clientName || row.company || row.name),
        getPmAgentText(row.summary || row.address || row.projectAddress),
    ].filter(Boolean).join(' · ');
    const metrics = [
        getPmAgentText(row.status) ? `status ${getPmAgentText(row.status)}` : '',
        getPmAgentText(row.suburb || row.projectSuburb),
        getPmAgentText(row.dueDate) ? `due ${getPmAgentText(row.dueDate)}` : '',
        getPmAgentText(row.createdAt || row.createdDate) ? `created ${getPmAgentText(row.createdAt || row.createdDate)}` : '',
    ].filter(Boolean);

    return {
        key: `${domain}-${recordId ?? index}`,
        item: match?.item || buildQuickAiItemFromAgentRow(domain, row, recordId, title, lead),
        title,
        lead,
        metrics,
        confidenceLabel: match?.confidenceLabel || null,
    };
}

function buildQuickAiReturnedRowCards(
    result: CompanyAiQueryResult | null,
    rankedMatches: RankedQuickAiPmMatch[]
): QuickAiReturnedRowCard[] {
    const collection = getQuickAiAgentCollection(result);
    if (!collection?.rows.length) {
        return rankedMatches.map((match, index) => {
            const summaryParts = splitQuickAiSummaryParts(match.item.summary);
            return {
                key: `${match.item.domain}-${match.item.recordId ?? index}`,
                item: match.item,
                title: match.item.title,
                lead: summaryParts.leadParts.length ? summaryParts.leadParts.join(' • ') : match.item.summary,
                metrics: summaryParts.metricParts.slice(0, 4),
                confidenceLabel: match.confidenceLabel,
            };
        });
    }

    const matchesById = new Map<string, RankedQuickAiPmMatch>();
    rankedMatches.forEach((match) => {
        if (match.item.recordId != null) {
            matchesById.set(`${match.item.domain}:${match.item.recordId}`, match);
        }
    });

    return collection.rows.map((row, index) => {
        const recordId = getQuickAiAgentRowId(collection.domain, row);
        const match = recordId != null ? matchesById.get(`${collection.domain}:${recordId}`) : undefined;
        return buildQuickAiRawRowCard(collection.domain, row, index, match);
    });
}

function buildQuickAiMatchCountLabel(count: number) {
    if (!Number.isFinite(count) || count <= 0) {
        return null;
    }

    return `${count} match${count === 1 ? '' : 'es'}`;
}

function buildDashboardWarningSignature(scope: string, warnings: unknown): string | null {
    if (!Array.isArray(warnings) || !warnings.length) {
        return null;
    }

    const normalizedWarnings = warnings
        .map((warning) => {
            if (warning && typeof warning === 'object') {
                const section = typeof (warning as any).section === 'string' ? (warning as any).section : 'unknown';
                const message = typeof (warning as any).message === 'string' ? (warning as any).message : '';
                return `${section}:${message}`;
            }
            return String(warning);
        })
        .filter(Boolean);

    if (!normalizedWarnings.length) {
        return null;
    }

    return `${scope}|${normalizedWarnings.join('|')}`;
}

const Home: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user, isAuthenticated } = useAuth();
    const isNarrowScreen = useIsNarrowScreen(640);
    const showDesktopNotificationsPane = useMediaQuery('(min-width: 1440px)');
    const [recentColumns, setRecentColumns] = useState<any[]>([]);
    const [dashboardCardStats, setDashboardCardStats] = useState(createInitialDashboardCardStats);
    const [quickAiPrompt, setQuickAiPrompt] = useState('');
    const [quickAiStructuredMatch, setQuickAiStructuredMatch] = useState<CompanyAiQueryResult | null>(null);
    const [quickAiLoading, setQuickAiLoading] = useState(false);
    const [quickAiError, setQuickAiError] = useState<string | null>(null);
    const [quickAiSubmittedPrompt, setQuickAiSubmittedPrompt] = useState<string | null>(null);
    const [quickAiAskedAt, setQuickAiAskedAt] = useState<string | null>(null);
    const [quickAiSavedAt, setQuickAiSavedAt] = useState<string | null>(null);
    const loggedDashboardWarningSignaturesRef = useRef<Set<string>>(new Set());
    const [activityLoading, setActivityLoading] = useState(true);
    const [activityLastRefreshedAt, setActivityLastRefreshedAt] = useState<Date | null>(null);
    const isAdmin = hasAdminEquivalentAccess(user?.userRoles, user?.userDetails);
    const normalizedRoles = normalizeRoles(user?.userRoles);
    const normalizedUserEmail = normalizeEmail(user?.userDetails);
    const canAccessPmModules = isAdmin || normalizedRoles.includes('pm');
    const canAccessFinanceModules = isAdmin || normalizedRoles.includes('finance');
    const canAccessAiWorkspace = isAdmin || normalizedRoles.includes('pm') || normalizedRoles.includes('finance');
    const companyAiEnabled = isCompanyAiEnabled();
    const quickAiRenderableStructuredCandidates = getRenderableStructuredCandidates(quickAiStructuredMatch);
    const quickAiPreferredDomains = getQuickAiPreferredDomains(quickAiSubmittedPrompt || quickAiPrompt, quickAiStructuredMatch);
    const quickAiRankedPmMatches = buildRankedQuickAiStructuredMatches(
        quickAiRenderableStructuredCandidates,
        quickAiStructuredMatch ? {
            entityType: quickAiStructuredMatch.selectedEntityType,
            entityId: quickAiStructuredMatch.selectedEntityId,
        } : null,
        quickAiPreferredDomains
    );
    const quickAiCollectionTarget = buildQuickAiCollectionNavigationTarget(quickAiStructuredMatch);
    const quickAiTopMatch = quickAiCollectionTarget ? null : (quickAiRankedPmMatches[0] || null);
    const quickAiReturnedRows = buildQuickAiReturnedRowCards(quickAiStructuredMatch, quickAiRankedPmMatches).slice(0, isNarrowScreen ? 5 : 6);
    const quickAiShowReturnedRows = quickAiReturnedRows.length > 1;
    const quickAiResultBadgeLabel = buildQuickAiResultBadgeLabel(quickAiTopMatch, quickAiRenderableStructuredCandidates.length, quickAiCollectionTarget);
    const quickAiExplanationCaption = buildQuickAiCollectionCaption(quickAiCollectionTarget) || buildQuickAiTopMatchCaption(quickAiTopMatch);
    const quickAiDisplaySections = buildQuickAiStructuredSections(quickAiStructuredMatch);
    const quickAiAnswerNavigationTarget = buildQuickAiAnswerNavigationTarget(quickAiStructuredMatch, quickAiTopMatch, quickAiCollectionTarget);
    const quickAiHasReplyMeta = Boolean(quickAiStructuredMatch && (quickAiStructuredMatch.confidence !== null || quickAiResultBadgeLabel));
    const canAccessQuotationModule = hasRequiredRoleAccess(user?.userRoles, user?.userDetails, ['finance']);
    const shouldShowInvoiceDashboardCard = canAccessFinanceModules || INVOICE_DASHBOARD_SPECIAL_EMAILS.has(normalizedUserEmail);
    const openAiWorkspace = (promptText?: string) => {
        const trimmedPrompt = String(promptText || '').trim();
        navigate(trimmedPrompt ? `/ai?prompt=${encodeURIComponent(trimmedPrompt)}` : '/ai', {
            state: trimmedPrompt ? {
                companyAiFollowUp: {
                    prompt: trimmedPrompt,
                    conversationTail: [],
                },
            } : undefined,
        });
    };

    const handleQuickAiSubmit = (event: React.FormEvent) => {
        void (async () => {
            event.preventDefault();
            const trimmedPrompt = quickAiPrompt.trim();
            if (!trimmedPrompt) {
                setQuickAiError('Enter a PM Agent question before running it.');
                return;
            }

            setQuickAiLoading(true);
            setQuickAiError(null);

            try {
                const submittedAt = new Date().toISOString();
                const pmAgentResult = await pmAgentService.query({
                    message: trimmedPrompt,
                });
                const structuredMatchResult = mapPmAgentResultToQuickQueryResult(trimmedPrompt, pmAgentResult);
                setQuickAiStructuredMatch(structuredMatchResult);
                setQuickAiSubmittedPrompt(trimmedPrompt);
                setQuickAiAskedAt(submittedAt);
                setQuickAiSavedAt(new Date().toISOString());
            } catch (error) {
                setQuickAiError(normalizeErrorMessage(error));
            } finally {
                setQuickAiLoading(false);
            }
        })();
    };

    const handleQuickAiTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
            return;
        }

        event.preventDefault();

        if (quickAiLoading) {
            return;
        }

        event.currentTarget.form?.requestSubmit();
    };

    const handleOpenWorkspace = () => {
        const trimmedPrompt = String(quickAiPrompt || '').trim();
        if (trimmedPrompt) {
            openAiWorkspace(trimmedPrompt);
            return;
        }

        navigate('/ai');
    };

    const handleOpenAiPrimarySite = () => {
        if (typeof window === 'undefined') {
            return;
        }

        window.location.href = getCompanyAiValidationUrl('/ai');
    };

    const handleQuickAiReset = () => {
        setQuickAiPrompt('');
        setQuickAiStructuredMatch(null);
        setQuickAiError(null);
        setQuickAiSubmittedPrompt(null);
        setQuickAiAskedAt(null);
        setQuickAiSavedAt(null);
    };

    const handleOpenQuickAiMatch = (item: CompanyAiPmContextItem) => {
        const target = buildCompanyAiMatchNavigation(item, {
            prompt: quickAiPrompt,
            conversationTail: [],
        });
        navigate(target.pathname, { state: target.state });
    };

    const applyRecentActivityCache = (cacheEntry: HomeCacheEntry<HomeRecentActivityCache>) => {
        setRecentColumns(Array.isArray(cacheEntry.data?.recentColumns) ? cacheEntry.data.recentColumns : []);
        setActivityLastRefreshedAt(
            cacheEntry.data?.activityLastRefreshedAt ? new Date(cacheEntry.data.activityLastRefreshedAt) : null
        );
        setActivityLoading(false);
    };

    useEffect(() => {
        if (!isAuthenticated || !user) {
            setDashboardCardStats(createInitialDashboardCardStats());
            return;
        }

        const cachedStats = readHomeCache<ReturnType<typeof createInitialDashboardCardStats>>(HOME_DASHBOARD_STATS_CACHE_KEY);
        if (cachedStats) {
            setDashboardCardStats(cachedStats.data);
        }

        if (!isHomeCacheFresh(cachedStats)) {
            void loadDashboardCardStats();
        }
    }, [isAuthenticated, user]);

    // Load recent activities from database
    useEffect(() => {
        if (!isAuthenticated || !user) {
            setRecentColumns([]);
            setActivityLastRefreshedAt(null);
            setActivityLoading(false);
            return;
        }

        const cachedActivity = readHomeCache<HomeRecentActivityCache>(HOME_RECENT_ACTIVITY_CACHE_KEY);
        if (cachedActivity) {
            applyRecentActivityCache(cachedActivity);
        } else {
            setActivityLoading(false);
        }

        if (isHomeCacheFresh(cachedActivity)) {
            return;
        }

        void loadRecentActivities();
    }, [isAuthenticated, user]);

    const loadDashboardCardStats = async () => {
        try {
            const { azureSqlService } = await import('@/services/azureSqlService.js');
            const dashboard = await azureSqlService.getHomeDashboard();
            const nextStats = dashboard?.stats || createInitialDashboardCardStats();
            const degraded = isDegradedDashboardPayload(dashboard);
            const cachedStats = readHomeCache<ReturnType<typeof createInitialDashboardCardStats>>(HOME_DASHBOARD_STATS_CACHE_KEY);

            if (!(degraded && cachedStats)) {
                setDashboardCardStats(nextStats);
            }

            if (!degraded) {
                writeHomeCache(HOME_DASHBOARD_STATS_CACHE_KEY, nextStats);
            } else if (import.meta.env.DEV) {
                const warningSignature = buildDashboardWarningSignature('stats', dashboard?.warnings);
                if (warningSignature && !loggedDashboardWarningSignaturesRef.current.has(warningSignature)) {
                    loggedDashboardWarningSignaturesRef.current.add(warningSignature);
                    console.info('Dashboard stats response is degraded; keeping stats cache unchanged.', dashboard.warnings);
                }
            }
        } catch (error) {
            console.error('Error loading dashboard stats:', error);
        }
    };

    const loadRecentActivities = async (forceRefresh = false) => {
        const cachedActivity = readHomeCache<HomeRecentActivityCache>(HOME_RECENT_ACTIVITY_CACHE_KEY);
        if (!forceRefresh) {
            if (isHomeCacheFresh(cachedActivity) && cachedActivity) {
                applyRecentActivityCache(cachedActivity);
                return;
            }
        }

        setActivityLoading(true);
        const columns: any[] = [];

        try {
            const { azureSqlService } = await import('@/services/azureSqlService.js');
            const dashboard = await azureSqlService.getHomeDashboard();
            const degraded = isDegradedDashboardPayload(dashboard);
            const cachedStats = readHomeCache<ReturnType<typeof createInitialDashboardCardStats>>(HOME_DASHBOARD_STATS_CACHE_KEY);
            if (dashboard?.stats) {
                if (!(degraded && cachedStats)) {
                    setDashboardCardStats(dashboard.stats);
                }
                if (!degraded) {
                    writeHomeCache(HOME_DASHBOARD_STATS_CACHE_KEY, dashboard.stats);
                }
            }
            const recentData = dashboard?.recent || {};

            const asDate = (value: any) => {
                if (!value) return null;
                const d = new Date(value);
                return Number.isNaN(d.getTime()) ? null : d;
            };

            const daysAgo = (value: any) => {
                const d = asDate(value);
                if (!d) return null;
                const diff = Date.now() - d.getTime();
                return diff >= 0 ? Math.floor(diff / (1000 * 60 * 60 * 24)) : 0;
            };

            const normalizePostCode = (value: any) => {
                if (value === null || value === undefined) return '';
                if (typeof value === 'number') return String(Math.trunc(value));
                const str = String(value).trim();
                const n = Number(str);
                if (!Number.isNaN(n) && Number.isFinite(n)) return String(Math.trunc(n));
                return str;
            };

            const formatShortDate = (value: any) => {
                const d = asDate(value);
                if (!d) return '—';
                return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
            };

            const formatMoney = (amount: any) => {
                const n = typeof amount === 'number' ? amount : Number(String(amount ?? '').trim());
                const value = Number.isFinite(n) ? n : 0;
                return new Intl.NumberFormat('en-AU', {
                    style: 'currency',
                    currency: 'AUD',
                    maximumFractionDigits: 0,
                }).format(value);
            };

            const formatEditedAt = (value: any) => {
                const d = asDate(value);
                if (!d) return '—';

                const now = new Date();
                if (d.toDateString() === now.toDateString()) {
                    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
                }

                return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
            };

            const statusColor = (status: string) => {
                const s = (status || '').toLowerCase();
                if (s === 'accepted' || s === 'approved' || s === 'paid' || s === 'issued') return '#0F8A63';
                if (s === 'pending' || s === 'sent' || s === 'in progress') return '#C06C16';
                if (s === 'overdue' || s === 'rejected' || s === 'expired') return '#C93535';
                return '#5B6472';
            };

            const formatTaskGlance = (task: any) => {
                const priority = String(task?.Priority || '').trim();
                const dueLabel = task?.DueDate
                    ? `Due ${formatShortDate(task.DueDate)}`
                    : (task?.ProjectNumber || task?.ProjectName || '—');

                return priority ? `${priority} • ${dueLabel}` : dueLabel;
            };
            const results: any[] = [];
            if (canAccessFinanceModules) {
                results.push(
                    {
                        data: Array.isArray(recentData.quotations) ? recentData.quotations : [],
                        entityName: 'Quotations',
                        icon: '📋',
                        color: 'linear-gradient(135deg, #173b84 0%, #2251b4 100%)',
                    },
                    {
                        data: Array.isArray(recentData.invoices) ? recentData.invoices : [],
                        entityName: 'Invoices',
                        icon: '🧾',
                        color: 'linear-gradient(135deg, #2251b4 0%, #4b6fbc 100%)',
                    },
                    {
                        data: Array.isArray(recentData.clients) ? recentData.clients : [],
                        entityName: 'Clients',
                        icon: '👥',
                        color: 'linear-gradient(135deg, #52627d 0%, #334155 100%)',
                    }
                );
            }

            if (canAccessPmModules) {
                results.push(
                    {
                        data: Array.isArray(recentData.projects) ? recentData.projects : [],
                        entityName: 'Projects',
                        icon: '🏗️',
                        color: 'linear-gradient(135deg, #173b84 0%, #2251b4 100%)',
                    },
                    {
                        data: Array.isArray(recentData.tasks) ? recentData.tasks : [],
                        entityName: 'Tasks',
                        icon: '✅',
                        color: 'linear-gradient(135deg, #244487 0%, #2d5ac8 100%)',
                    },
                    {
                        data: Array.isArray(recentData.certificates) ? recentData.certificates : [],
                        entityName: 'Certificates',
                        icon: '🏆',
                        color: 'linear-gradient(135deg, #1f6f8b 0%, #2251b4 100%)',
                    }
                );
            }

            const makeColumn = (payload: any, route: string, mapRow: (x: any) => any) => {
                columns.push({
                    key: payload.entityName,
                    title: payload.entityName,
                    icon: payload.icon,
                    color: payload.color,
                    route,
                    rows: (payload.data || []).slice(0, 5).map(mapRow),
                    error: payload.error,
                });
            };

            results.forEach((payload) => {
                if (payload.entityName === 'Quotations') {
                    makeColumn(payload, '/quotations', (q: any) => {
                        const location = q.ProjectLocation || [q.Address, q.Suburb, normalizePostCode(q.PostCode)].filter(Boolean).join(', ');
                        const structural = Number(q.StructuralFee) || 0;
                        const civil = Number(q.CivilFee) || 0;
                        const total = Number(q.SubTotal) || (structural + civil);
                        const when = q.ModifiedDate || q.CreatedDate || q.Date;
                        const status = q.Acceptance || q.Status || 'Pending';
                        return {
                            id: q.QuotationId ?? q.Id ?? q.QuotationNumber,
                            primary: q.QuotationNumber || q.QuotationNo || '—',
                            secondary: location || '—',
                            right: formatEditedAt(when),
                            glance: formatMoney(total),
                            status,
                            statusColor: statusColor(status),
                            ageDays: String(status).toLowerCase() === 'accepted' ? null : daysAgo(when),
                            to: q.QuotationId ? `/quotations/${q.QuotationId}` : '/quotations',
                        };
                    });
                    return;
                }

                if (payload.entityName === 'Invoices') {
                    makeColumn(payload, '/invoices', (inv: any) => {
                        const when = inv.ModifiedDate || inv.PaidDate || inv.Created || inv.CreatedDate;
                        const status = inv.Status || (inv.PaidYN ? 'Paid' : 'Draft');
                        return {
                            id: inv.InvoiceId ?? inv.Id ?? inv.InvoiceNumber,
                            primary: inv.InvoiceNumber || inv.InvoiceNo || '—',
                            secondary: inv.Description || inv.ProjectAddress || inv.Client || '—',
                            right: formatEditedAt(when),
                            glance: inv.TotalAmount != null ? formatMoney(inv.TotalAmount) : '—',
                            status,
                            statusColor: statusColor(status),
                            ageDays: String(status).toLowerCase() === 'paid' ? null : daysAgo(when),
                            to: inv.InvoiceId ? `/invoices/${inv.InvoiceId}` : '/invoices',
                        };
                    });
                    return;
                }

                if (payload.entityName === 'Clients') {
                    makeColumn(payload, '/clients', (cl: any) => {
                        const when = cl.ModifiedDate || cl.CreatedDate;
                        const status = cl.Status || 'Active';
                        return {
                            id: cl.ClientId,
                            primary: cl.Name || cl.Client || '—',
                            secondary: cl.Email || cl.Phone || cl.Address || '—',
                            right: formatEditedAt(when),
                            glance: cl.Phone || cl.Address || '—',
                            status,
                            statusColor: statusColor(status),
                            ageDays: null,
                            to: '/clients',
                        };
                    });
                    return;
                }

                if (payload.entityName === 'Projects') {
                    makeColumn(payload, '/projects', (p: any) => {
                        const when = p.UpdatedAt || p.ModifiedDate || p.CreatedAt || p.CreatedDate;
                        const status = p.Status || 'Active';
                        return {
                            id: p.ProjectId,
                            primary: p.ProjectCode || p.ProjectNumber || '—',
                            secondary: p.ProjectName || p.Title || p.SiteAddress || '—',
                            right: formatEditedAt(when),
                            glance: p.SiteAddress || p.Suburb || '—',
                            status,
                            statusColor: statusColor(status),
                            ageDays: null,
                            to: p.ProjectId ? `/projects/${p.ProjectId}` : '/projects',
                        };
                    });
                    return;
                }

                if (payload.entityName === 'Tasks') {
                    makeColumn(payload, '/tasks', (task: any) => {
                        const when = task.ModifiedDate || task.CreatedDate || task.DueDate;
                        const status = task.Status || 'To Do';
                        return {
                            id: task.TaskId,
                            primary: task.TaskNumber || `T${task.TaskId}`,
                            secondary: task.Title || task.TaskName || task.Description || '—',
                            right: formatEditedAt(when),
                            glance: formatTaskGlance(task),
                            status,
                            statusColor: statusColor(status),
                            ageDays: String(status).toLowerCase() === 'completed' ? null : daysAgo(when),
                            to: '/tasks',
                        };
                    });
                    return;
                }

                if (payload.entityName === 'Certificates') {
                    makeColumn(payload, '/certificates', (c: any) => {
                        const when = c.ModifiedDate || c.CreatedDate || c.IssueDate;
                        const status = c.Status || 'Draft';
                        const projectNumber = c.ProjectNumber || c.ProjectCode || c.projectNumber || c.projectCode;
                        const projectAddress = c.ProjectAddress
                            || c.ProjectLocation
                            || c.Address
                            || c.SiteAddress
                            || c.projectLocation
                            || c.projectAddress
                            || c.address
                            || c.Title;
                        const revision = c.RevisionString
                            || (c.LastVersionNumber && c.VersionTag ? `Rev ${c.LastVersionNumber} - ${c.VersionTag}` : null)
                            || (c.LastVersionNumber ? `Rev ${c.LastVersionNumber}` : null)
                            || (c.VersionTag ? `Rev ${c.VersionTag}` : null);
                        return {
                            id: c.CertificateId,
                            primary: c.CertificateNumber || '—',
                            secondary: [projectNumber, projectAddress].filter(Boolean).join(' • ') || '—',
                            right: formatEditedAt(when),
                            glance: revision || (c.IssueDate ? `Issued ${formatShortDate(c.IssueDate)}` : '—'),
                            status,
                            statusColor: statusColor(status),
                            ageDays: null,
                            to: '/certificates',
                        };
                    });
                }
            });

            const refreshedAt = dashboard?.activityLastRefreshedAt ? new Date(dashboard.activityLastRefreshedAt) : new Date();
            if (!(degraded && cachedActivity)) {
                setRecentColumns(columns);
                setActivityLastRefreshedAt(refreshedAt);
            }
            if (!degraded) {
                writeHomeCache(HOME_RECENT_ACTIVITY_CACHE_KEY, {
                    recentColumns: columns,
                    activityLastRefreshedAt: refreshedAt.toISOString(),
                });
            } else if (import.meta.env.DEV) {
                const warningSignature = buildDashboardWarningSignature('recent', dashboard?.warnings);
                if (warningSignature && !loggedDashboardWarningSignaturesRef.current.has(warningSignature)) {
                    loggedDashboardWarningSignaturesRef.current.add(warningSignature);
                    console.info('Dashboard recent activity response is degraded; keeping activity cache unchanged.', dashboard.warnings);
                }
            }
        } catch (error) {
            console.error('Error loading recent activities:', error);
        } finally {
            setActivityLoading(false);
        }
    };

    // WORKFLOW CARDS CONFIGURATION
    // Each card represents a main module in the application
    // TO CUSTOMIZE: Edit colors, icons, stats, or add new cards here
    const roleLabel = isAuthenticated
      ? getPrimaryRoleLabel(user?.userRoles, user?.userDetails)
      : 'anonymous';
    const userLabel = isAuthenticated ? (user?.userDetails || 'Unknown') : 'Not logged in';

    const cards = {
        controlCentre: {
            title: 'Project Control Centre',
            description: 'Plan work, check team capacity and review delivery risk',
            icon: '🗓️',
            color: 'linear-gradient(135deg, #1f6f8b 0%, #2251b4 100%)',
            route: '/project-control-centre',
            stats: []
        },
        projects: {
            title: t.navigation.projects,
            description: 'Active project management',
            icon: '🏗️',
            color: 'linear-gradient(135deg, #173b84 0%, #2251b4 100%)',
            route: '/projects',
            stats: dashboardCardStats.projects
        },
        tasks: {
            title: t.navigation.tasks,
            description: 'Task management',
            icon: '✅',
            color: 'linear-gradient(135deg, #244487 0%, #2d5ac8 100%)',
            route: '/tasks',
            stats: dashboardCardStats.tasks
        },
        quotations: {
            title: t.navigation.quotations,
            description: 'Manage client quotes',
            icon: '📋',
            color: 'linear-gradient(135deg, #2251b4 0%, #355fbf 100%)',
            route: '/quotations',
            stats: dashboardCardStats.quotations
        },
        invoices: {
            title: t.navigation.invoices,
            description: 'Billing and payments',
            icon: '🧾',
            color: 'linear-gradient(135deg, #2251b4 0%, #4b6fbc 100%)',
            route: '/invoices',
            stats: dashboardCardStats.invoices
        },
        certificates: {
            title: t.navigation.certificates,
            description: 'Certificate management',
            icon: '🏆',
            color: 'linear-gradient(135deg, #1f6f8b 0%, #2251b4 100%)',
            route: '/certificates',
            stats: dashboardCardStats.certificates
        },
        clients: {
            title: t.navigation.clients,
            description: 'Client management',
            icon: '👥',
            color: 'linear-gradient(135deg, #52627d 0%, #334155 100%)',
            route: '/clients',
            stats: dashboardCardStats.clients
        }
    } as const;

    // Order is row-major for a 3-column grid, so columns pair as:
    // (Projects, Tasks) (Quotations, Invoices) (Certificates, Clients)
    const workflowCards = isAdmin
        ? [cards.projects, cards.quotations, cards.certificates, cards.tasks, cards.invoices, cards.clients]
        : [
            ...(canAccessPmModules ? [cards.projects, cards.tasks] : []),
            ...(canAccessQuotationModule ? [cards.quotations] : []),
            ...(shouldShowInvoiceDashboardCard ? [cards.invoices] : []),
        ];

    const layoutStyles = {
        container: {
            maxWidth: '1200px',
            margin: '0 auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            boxSizing: 'border-box'
        } as React.CSSProperties,
        sectionHeader: {
            padding: isNarrowScreen ? '16px 0 14px 0' : '20px 0 16px 0',
            display: 'flex',
            alignItems: isNarrowScreen ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
        } as React.CSSProperties,
        sectionTitle: {
            fontSize: isNarrowScreen ? '20px' : '22px',
            fontWeight: '600',
            color: 'var(--color-text)',
            margin: '0'
        } as React.CSSProperties,
        workflowGrid: {
            display: 'grid',
            gap: '10px',
            marginBottom: '18px'
        } as React.CSSProperties,
        recentSection: {
            marginTop: '24px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
        } as React.CSSProperties,
        itemList: {
            background: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-xs)',
            border: '1px solid var(--color-border)'
        } as React.CSSProperties,
        listItem: {
            padding: '16px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            transition: 'background-color 0.2s ease'
        } as React.CSSProperties,
        itemIcon: {
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '16px',
            fontSize: '16px'
        } as React.CSSProperties,
        itemContent: {
            flex: '1'
        } as React.CSSProperties,
        itemTitle: {
            fontSize: '15px',
            fontWeight: '500',
            color: 'var(--color-text)',
            marginBottom: '2px'
        } as React.CSSProperties,
        itemSubtitle: {
            fontSize: '13px',
            color: 'var(--color-text-muted)'
        } as React.CSSProperties,
        statusBadge: {
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '500',
            textTransform: 'uppercase' as const,
            background: 'var(--color-warning-soft)',
            color: 'var(--color-warning)'
        } as React.CSSProperties
    };

    /*
     * MAIN PAGE RENDER
     * ===============
     * This section defines the actual HTML structure and layout of the page
     */
    return (
        <div className="home-shell">
            {/* 
             * PAGE HEADER SECTION
             * ==================
             * The blue bar at the top with "Dashboard" title
             */}
            <header className="home-topbar">
                <div className="home-topbar__identity">
                    <h1>Dashboard</h1>
                    <span>{roleLabel}</span>
                </div>

                <div className="home-topbar__actions">
                    {canAccessAiWorkspace && companyAiEnabled ? (
                        <CompanyAiQuickMenu userEmail={user?.userDetails} userRoles={user?.userRoles} />
                    ) : null}
          {!showDesktopNotificationsPane ? <UserNotificationsBell showAssistantSuggestions /> : null}
                    <div className="home-user-summary">
                        <span>User: {userLabel}</span>
                        <span>Role: {roleLabel}</span>
                    </div>
                </div>
            </header>
            
            {/* 
             * MAIN CONTENT CONTAINER
             * =====================
             * Centers content with maximum width for larger screens
             */}
            <div className={`home-dashboard-layout${showDesktopNotificationsPane ? ' home-dashboard-layout--with-notifications' : ''}`}>
            {showDesktopNotificationsPane ? (
                <aside className="home-notifications-rail" aria-label="Desktop notifications">
              <UserNotificationsBell variant="panel" showAssistantSuggestions />
                </aside>
            ) : null}

            <main className="home-main" style={layoutStyles.container}>

            {/*
             * WORKFLOW CARDS GRID
             * ==================
             * This section renders all the module cards in a responsive grid
             * Cards automatically wrap to new rows on smaller screens
             */}
            <div className="workflow-grid" style={layoutStyles.workflowGrid}>
                {canAccessPmModules && (
                    <Link to={cards.controlCentre.route} className="home-workflow-card home-control-centre-link">
                        <div className="home-workflow-card__main">
                            <span className="home-workflow-card__icon" aria-hidden="true">{cards.controlCentre.icon}</span>
                            <div className="home-workflow-card__copy">
                                <h2>{cards.controlCentre.title}</h2>
                                <p>{cards.controlCentre.description}</p>
                            </div>
                            <ChevronRight aria-hidden="true" />
                        </div>
                    </Link>
                )}
                {workflowCards.map((card, index) => (
                    <button
                        key={index}
                        type="button"
                        className="home-workflow-card"
                        style={{ '--home-card-accent': card.color } as React.CSSProperties}
                        onClick={() => navigate(card.route)}    // Navigate to the module when clicked
                    >
                        <div className="home-workflow-card__main">
                            <div className="home-workflow-card__icon">
                                {card.icon}
                            </div>
                            <div className="home-workflow-card__copy">
                                <h3>{card.title}</h3>
                                <p>{card.description}</p>
                            </div>
                        </div>
                        <div className="home-workflow-card__stats">
                            {card.stats.map((stat, statIndex) => (
                                <div key={statIndex} className="home-workflow-card__stat">
                                    <strong>{stat.value}</strong>
                                    <span>{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </button>
                ))}
            </div>

            {canAccessAiWorkspace && companyAiEnabled ? (
                <section className="home-ai-quick-card" aria-label="PM Agent quick access">
                    <div className="home-ai-quick-card__header">
                        <div className="home-ai-quick-card__icon">🧠</div>
                        <div className="home-ai-quick-card__copy">
                            <h2 className="home-ai-quick-card__title">PM Agent Quick Access</h2>
                            <p className="home-ai-quick-card__subtitle">
                                Run a fast read-only PMapp lookup here. Open Company AI when you need broader chat, company-file guidance, or deeper follow-up.
                            </p>
                        </div>
                    </div>

                    <div className="home-ai-quick-card__body">
                        <form className="home-ai-quick-card__form home-ai-quick-card__surface" onSubmit={handleQuickAiSubmit}>
                            <div className="home-ai-quick-card__section-head">
                                <div>
                                    <div className="home-ai-quick-card__eyebrow">Input</div>
                                    <div className="home-ai-quick-card__panel-title">Run PM Agent</div>
                                </div>
                                <div className="home-ai-quick-card__section-note">
                                    Best for read-only PMapp lookups, summaries, and direct row navigation.
                                </div>
                            </div>
                            <textarea
                                className="home-ai-quick-card__textarea"
                                aria-label="PM Agent quick question"
                                value={quickAiPrompt}
                                onChange={(event) => {
                                    setQuickAiPrompt(event.target.value);
                                    if (quickAiError) {
                                        setQuickAiError(null);
                                    }
                                }}
                                onKeyDown={handleQuickAiTextareaKeyDown}
                                placeholder="Example: invoice 26012-1, 26012 quotation, Smith Box Hill project, or task for 12 Separation Street"
                                rows={isNarrowScreen ? 4 : 2}
                            />
                            <div className="home-ai-quick-card__composer-footer">
                                <div className="home-ai-quick-card__helper">
                                    Uses PM Agent's read-only tool registry for projects, invoices, quotations, tasks, clients, and summaries.
                                </div>
                                <div className="home-ai-quick-card__actions">
                                    <button type="submit" className="home-ai-quick-card__primary" disabled={quickAiLoading}>
                                        {quickAiLoading ? 'Checking…' : 'Run PM Agent'}
                                    </button>
                                    <button
                                        type="button"
                                        className="home-ai-quick-card__secondary"
                                        onClick={handleOpenWorkspace}
                                    >
                                        Open Company AI
                                    </button>
                                    <button
                                        type="button"
                                        className="home-ai-quick-card__tertiary"
                                        onClick={handleQuickAiReset}
                                        disabled={quickAiLoading}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            {quickAiError ? (
                                <div className="home-ai-quick-card__feedback home-ai-quick-card__feedback--error">
                                    {quickAiError}
                                </div>
                            ) : null}
                        </form>

                        <aside className="home-ai-quick-card__reply home-ai-quick-card__surface" aria-live="polite">
                            <div className="home-ai-quick-card__section-head">
                                <div>
                                    <div className="home-ai-quick-card__eyebrow">Reply</div>
                                    <div className="home-ai-quick-card__panel-title">
                                        {quickAiStructuredMatch ? 'Latest PM Agent result' : 'Reply will appear here'}
                                    </div>
                                </div>
                                <div className="home-ai-quick-card__section-note">
                                    Structured PMapp answer with direct row navigation where a row is returned.
                                </div>
                            </div>
                            {quickAiLoading ? (
                                <div className="home-ai-quick-card__reply-placeholder">
                                    <div className="home-ai-quick-card__reply-spinner">⏳</div>
                                    <div>PM Agent is checking read-only PMapp data for this dashboard card…</div>
                                </div>
                            ) : quickAiStructuredMatch ? (
                                <div className="home-ai-quick-card__reply-content">
                                    <div className="home-ai-quick-card__reply-layout">
                                        <div className="home-ai-quick-card__reply-main-shell">
                                            {quickAiHasReplyMeta ? (
                                                <div className="home-ai-quick-card__reply-meta">
                                                    {quickAiStructuredMatch.confidence !== null ? (
                                                        <span className="home-ai-quick-card__meta-pill">
                                                            Confidence: {Math.round((quickAiStructuredMatch.confidence || 0) * 100)}%
                                                        </span>
                                                    ) : null}
                                                    {quickAiResultBadgeLabel ? (
                                                        <span className="home-ai-quick-card__meta-pill home-ai-quick-card__meta-pill--match">
                                                            {quickAiResultBadgeLabel}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : null}

                                            <div className="home-ai-quick-card__reply-main">
                                                <div className="home-ai-quick-card__dialogue">
                                                    {quickAiSubmittedPrompt ? (
                                                        <article className="home-ai-quick-card__message home-ai-quick-card__message--user">
                                                            <div className="home-ai-quick-card__message-meta">
                                                                <strong>You</strong>
                                                                <span>{formatQuickAiTimestamp(quickAiAskedAt || quickAiSavedAt)}</span>
                                                            </div>
                                                            <div className="home-ai-quick-card__message-body">
                                                                {quickAiSubmittedPrompt}
                                                            </div>
                                                        </article>
                                                    ) : null}

                                                    <article className="home-ai-quick-card__message home-ai-quick-card__message--assistant">
                                                        <div className="home-ai-quick-card__message-meta">
                                                            <strong>PM Agent</strong>
                                                            <span>{formatQuickAiTimestamp(quickAiSavedAt)}</span>
                                                            {quickAiExplanationCaption ? (
                                                                <span className="home-ai-quick-card__message-caption">{quickAiExplanationCaption}</span>
                                                            ) : null}
                                                        </div>
                                                        <div className="home-ai-quick-card__reply-text">
                                                            {quickAiDisplaySections.map((section, index) => {
                                                                const navigationTarget = index === 0 ? quickAiAnswerNavigationTarget : null;
                                                                const showSectionHeading = section.heading && section.heading !== 'Answer';

                                                                if (navigationTarget) {
                                                                    return (
                                                                        <section
                                                                            key={`${section.heading}-${index}`}
                                                                            className="home-ai-quick-card__answer-section home-ai-quick-card__answer-section--interactive"
                                                                            role="button"
                                                                            tabIndex={0}
                                                                            onClick={() => handleOpenQuickAiMatch(navigationTarget.item)}
                                                                            onKeyDown={(event) => {
                                                                                if (event.key === 'Enter' || event.key === ' ') {
                                                                                    event.preventDefault();
                                                                                    handleOpenQuickAiMatch(navigationTarget.item);
                                                                                }
                                                                            }}
                                                                        >
                                                                            {showSectionHeading || navigationTarget.actionLabel ? (
                                                                                <div className={`home-ai-quick-card__answer-heading-row${showSectionHeading ? ' home-ai-quick-card__answer-heading-row--balanced' : ''}`}>
                                                                                    {showSectionHeading ? (
                                                                                        <div className="home-ai-quick-card__answer-heading">{section.heading}:</div>
                                                                                    ) : null}
                                                                                    <span className="home-ai-quick-card__answer-link">
                                                                                        {navigationTarget.actionLabel}
                                                                                    </span>
                                                                                </div>
                                                                            ) : null}
                                                                            <div className="home-ai-quick-card__answer-body">
                                                                                {renderQuickAiSectionBody(section.body)}
                                                                            </div>
                                                                        </section>
                                                                    );
                                                                }

                                                                return (
                                                                    <section key={`${section.heading}-${index}`} className="home-ai-quick-card__answer-section">
                                                                        {showSectionHeading ? (
                                                                            <div className="home-ai-quick-card__answer-heading">{section.heading}:</div>
                                                                        ) : null}
                                                                        <div className="home-ai-quick-card__answer-body">
                                                                            {renderQuickAiSectionBody(section.body)}
                                                                        </div>
                                                                    </section>
                                                                );
                                                            })}
                                                        </div>

                                                        {quickAiShowReturnedRows ? (
                                                            <div className="home-ai-quick-card__reply-section">
                                                                <div className="home-ai-quick-card__reply-label">Returned rows</div>
                                                                <div className="home-ai-quick-card__row-list">
                                                                    {quickAiReturnedRows.map((row, rowIndex) => {
                                                                        return (
                                                                            <button
                                                                                key={`${row.key}-${rowIndex}`}
                                                                                type="button"
                                                                                className="home-ai-quick-card__row"
                                                                                onClick={() => handleOpenQuickAiMatch(row.item)}
                                                                            >
                                                                                <div className="home-ai-quick-card__row-main">
                                                                                    <div className="home-ai-quick-card__row-title">
                                                                                        <strong>{row.title}</strong>
                                                                                        {row.confidenceLabel ? (
                                                                                            <span>{row.confidenceLabel}</span>
                                                                                        ) : null}
                                                                                    </div>
                                                                                    {row.lead ? (
                                                                                        <div className="home-ai-quick-card__row-lead">{row.lead}</div>
                                                                                    ) : null}
                                                                                </div>
                                                                                {row.metrics.length ? (
                                                                                    <div className="home-ai-quick-card__row-metrics">
                                                                                        {row.metrics.map((part, metricIndex) => (
                                                                                            <span key={`${row.key}-${metricIndex}`}>
                                                                                                {part}
                                                                                            </span>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : null}
                                                                                <div className="home-ai-quick-card__row-action">
                                                                                    {getQuickAiMatchActionLabel(row.item)}
                                                                                </div>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        ) : null}

                                                        {quickAiTopMatch && !quickAiShowReturnedRows ? (
                                                            <div className="home-ai-quick-card__reply-section">
                                                                <div className="home-ai-quick-card__reply-label">
                                                                    {quickAiStructuredMatch.actionType === 'clarify' ? 'Closest structured match' : 'Top PMapp match'}
                                                                </div>
                                                                <div className="home-ai-quick-card__match-list">
                                                                    {(() => {
                                                                        const { item, confidenceLabel, isTopMatch } = quickAiTopMatch;
                                                                        const summaryParts = splitQuickAiSummaryParts(item.summary);
                                                                        const isInteractive = quickAiStructuredMatch.actionType !== 'clarify';

                                                                        if (!isInteractive) {
                                                                            return (
                                                                                <div
                                                                                    key={`${item.domain}-${item.recordId}-top`}
                                                                                    className={`home-ai-quick-card__match${isTopMatch ? ' home-ai-quick-card__match--top' : ''}`}
                                                                                >
                                                                                    <div className="home-ai-quick-card__match-title-row">
                                                                                        <strong>{item.title}</strong>
                                                                                        {confidenceLabel ? (
                                                                                            <span className="home-ai-quick-card__match-score">{confidenceLabel} match</span>
                                                                                        ) : null}
                                                                                    </div>
                                                                                    {summaryParts.leadParts.length ? (
                                                                                        <div className="home-ai-quick-card__match-lead">
                                                                                            {summaryParts.leadParts.join(' • ')}
                                                                                        </div>
                                                                                    ) : null}
                                                                                    {summaryParts.metricParts.length ? (
                                                                                        <div className="home-ai-quick-card__match-metrics">
                                                                                            {summaryParts.metricParts.map((part, metricIndex) => (
                                                                                                <span key={`${item.domain}-${item.recordId}-${metricIndex}`} className="home-ai-quick-card__match-metric">
                                                                                                    {part}
                                                                                                </span>
                                                                                            ))}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span>{item.summary}</span>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <button
                                                                                key={`${item.domain}-${item.recordId}-top`}
                                                                                type="button"
                                                                                className={`home-ai-quick-card__match home-ai-quick-card__match--interactive${isTopMatch ? ' home-ai-quick-card__match--top' : ''}`}
                                                                                onClick={() => handleOpenQuickAiMatch(item)}
                                                                            >
                                                                                <div className="home-ai-quick-card__match-title-row">
                                                                                    <strong>{item.title}</strong>
                                                                                    {confidenceLabel ? (
                                                                                        <span className="home-ai-quick-card__match-score">{confidenceLabel} match</span>
                                                                                    ) : null}
                                                                                </div>
                                                                                {summaryParts.leadParts.length ? (
                                                                                    <div className="home-ai-quick-card__match-lead">
                                                                                        {summaryParts.leadParts.join(' • ')}
                                                                                    </div>
                                                                                ) : null}
                                                                                {summaryParts.metricParts.length ? (
                                                                                    <div className="home-ai-quick-card__match-metrics">
                                                                                        {summaryParts.metricParts.map((part, metricIndex) => (
                                                                                            <span key={`${item.domain}-${item.recordId}-${metricIndex}`} className="home-ai-quick-card__match-metric">
                                                                                                {part}
                                                                                            </span>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span>{item.summary}</span>
                                                                                )}
                                                                                <span className="home-ai-quick-card__match-link">{getQuickAiMatchActionLabel(item)}</span>
                                                                            </button>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                    </article>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="home-ai-quick-card__reply-footer">
                                        <span>Saved at {formatQuickAiTimestamp(quickAiSavedAt)}</span>
                                        <button
                                            type="button"
                                            className="home-ai-quick-card__link"
                                            onClick={handleOpenWorkspace}
                                        >
                                            Open in Company AI
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="home-ai-quick-card__reply-placeholder">
                                    <div className="home-ai-quick-card__reply-icon">💬</div>
                                    <div>Run PM Agent above, and the latest structured result will stay here for quick reference.</div>
                                </div>
                            )}
                        </aside>
                    </div>
                </section>
            ) : null}

            {canAccessAiWorkspace && !companyAiEnabled ? (
                <section className="home-ai-quick-card" aria-label="Company AI cutover notice">
                    <div className="home-ai-quick-card__header">
                        <div className="home-ai-quick-card__icon">🚧</div>
                        <div className="home-ai-quick-card__copy">
                            <h2 className="home-ai-quick-card__title">Company AI is hosted on the active AI PM site</h2>
                            <p className="home-ai-quick-card__subtitle">
                                This legacy PM deployment is frozen. Use the maintained AI PM site for current PMApp and Company AI work.
                            </p>
                        </div>
                    </div>
                    <div className="home-ai-quick-card__helper">
                        Use <strong>aipm.linfinityeng.com</strong> as the active PMApp environment.
                    </div>
                    <div className="home-ai-quick-card__actions">
                        <button type="button" className="home-ai-quick-card__primary" onClick={handleOpenAiPrimarySite}>
                            Open Active AI PM Site
                        </button>
                    </div>
                </section>
            ) : null}

            <section className="home-recent-section" style={layoutStyles.recentSection}>
                <div className="home-section-header" style={layoutStyles.sectionHeader}>
                    <h2 style={layoutStyles.sectionTitle}>Recent Activity</h2>
                    <div className="recent-actions">
                        {activityLastRefreshedAt ? (
                            <div className="recent-updatedAt">
                                Updated {activityLastRefreshedAt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        ) : null}
                        <button
                            type="button"
                            className="recent-refresh"
                            onClick={() => loadRecentActivities(true)}
                            disabled={activityLoading || !isAuthenticated}
                            aria-label="Refresh recent activity"
                            title="Refresh"
                        >
                            {activityLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>

                {activityLoading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                        <div>Loading recent activity...</div>
                    </div>
                ) : recentColumns.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📭</div>
                        <div>No recent activity</div>
                    </div>
                ) : (
                    <div className="recent-columns" aria-label="Recent activity columns">
                        {recentColumns.map((col) => (
                            <div key={col.key} className="recent-column">
                                <div
                                    className="recent-card"
                                    onClick={() => navigate(col.route)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') navigate(col.route);
                                    }}
                                >
                                    <div className="recent-card__header">
                                        <div className="recent-card__icon" style={{ '--recent-card-accent': col.color } as React.CSSProperties}>
                                            {col.icon}
                                        </div>
                                        <div className="recent-card__title">{col.title}</div>
                                    </div>

                                    {col.error ? (
                                        <div className="recent-card__empty">Not available</div>
                                    ) : col.rows.length === 0 ? (
                                        <div className="recent-card__empty">No entries</div>
                                    ) : (
                                        <div className="recent-card__rows">
                                            {col.rows.slice(0, 5).map((row: any, i: number) => (
                                                <div
                                                    key={`${row.id ?? i}`}
                                                    className="recent-row"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(row.to || col.route);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.stopPropagation();
                                                            navigate(row.to || col.route);
                                                        }
                                                    }}
                                                >
                                                    <div className="recent-row__main">
                                                        <div className="recent-row__primary">{row.primary}</div>
                                                        <div className="recent-row__secondary">{row.secondary}</div>
                                                    </div>
                                                    <div className="recent-row__meta">
                                                        <div className="recent-row__right">{row.right}</div>
                                                        <div className="recent-row__glance">{row.glance}</div>
                                                        <div className="recent-row__status" style={{ color: row.statusColor }}>
                                                            {row.status}
                                                            {row.ageDays !== null && row.ageDays !== undefined ? ` (${row.ageDays}d)` : ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
            </main>
            {showDesktopNotificationsPane ? <div className="home-dashboard-layout__balance" aria-hidden="true" /> : null}
            </div>
        </div>
    );
};

export default Home;
